import { useEffect, useState } from 'react';
import { MCP_SCOPE_DESCRIPTION, type ConnectedApp } from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { relativeTime } from '../../lib/time.js';
import { McpSetupDialog } from './McpSetupDialog.js';

/**
 * Every AI agent this user has connected, the button that cuts one off, and the entry point into
 * the setup walkthrough for connecting a new one.
 *
 * Disconnecting revokes the grant rather than waiting for tokens to expire, so it takes effect on
 * the agent's very next call — which is the only behaviour worth offering, since the reason
 * somebody clicks this is that they want the access gone now.
 */
export function ConnectedAppsCard() {
  const [apps, setApps] = useState<ConnectedApp[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);

  useEffect(() => {
    api
      .get<ConnectedApp[]>('/oauth/grants')
      .then(setApps)
      .catch(() => setError('Could not load connected apps.'));
  }, []);

  async function disconnect(app: ConnectedApp) {
    if (!window.confirm(`Disconnect “${app.clientName}”? It will lose access immediately.`)) return;
    setBusyId(app.id);
    try {
      await api.delete(`/oauth/grants/${encodeURIComponent(app.id)}`);
      setApps((prev) => (prev ?? []).filter((a) => a.id !== app.id));
    } catch {
      setError('Could not disconnect that app. Try again.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex flex-col gap-1.5">
          <CardTitle>Connected apps</CardTitle>
          <CardDescription>
            AI coding agents authorized to read your projects and update your tasks through the
            Pulse MCP connector.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 self-start"
          onClick={() => setSetupOpen(true)}
        >
          Connect an app
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error && <p className="text-sm text-danger">{error}</p>}
        {apps === null && !error && <p className="text-sm text-muted">Loading…</p>}
        {apps?.length === 0 && (
          <p className="text-sm text-muted">
            Nothing connected yet — click <strong>Connect an app</strong> above to get started.
          </p>
        )}
        {apps?.map((app) => (
          <div
            key={app.id}
            className="flex items-start justify-between gap-4 rounded-card border border-border p-3"
          >
            <div className="flex min-w-0 flex-col gap-1">
              <span className="truncate text-sm font-medium">{app.clientName}</span>
              <span className="text-xs text-muted">
                Connected {relativeTime(app.createdAt)}
                {app.lastUsedAt ? ` · last used ${relativeTime(app.lastUsedAt)}` : ' · never used'}
              </span>
              <ul className="mt-1 flex flex-col gap-0.5">
                {app.scopes.map((scope) => (
                  <li key={scope} className="text-xs text-muted">
                    · {MCP_SCOPE_DESCRIPTION[scope]}
                  </li>
                ))}
              </ul>
            </div>
            <Button
              variant="outline"
              className="shrink-0"
              disabled={busyId === app.id}
              onClick={() => void disconnect(app)}
            >
              {busyId === app.id ? 'Working…' : 'Disconnect'}
            </Button>
          </div>
        ))}
      </CardContent>
      <McpSetupDialog open={setupOpen} onClose={() => setSetupOpen(false)} />
    </Card>
  );
}
